import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { OldInput } from 'src/domains/layouts/shared/formComponents/input/OldInput';
import { useAppStateContext } from 'src/AppState/AppState';
import DatePicker from 'react-datepicker';
import { observable, makeAutoObservable } from 'mobx';
import {
    ErrorMessageBar,
    InputError,
    InputLabel,
} from 'src/domains/layouts/shared/formComponents/FormComponents.style';
import {
    FormComponent,
    FormWrapper,
    DatesWrapper,
    DateInputWrapper,
} from 'src/domains/layouts/shared/cms/form/Form.style';
import { TopNavigationLinkState } from 'src/AppState/TopNavigationLinkState/TopNavigationLinkState';
import { UploadImg } from 'src/components/UploadImg/UploadImg';
import { orderSelectionListOptions } from './orderSelectionListOptions';
import { OldSelect } from 'src/domains/layouts/shared/formComponents/Select/components/OldSelect';
import { directionOptions } from './directionOptions';
import { DestinanionOptionsPanel } from './DestinanionOptionsPanel';
import { visibilityOptions } from './visibilityOptions';
import { FormHeader } from 'src/domains/layouts/shared/cms/form/formHeader/FormHeader';
import { FormButtons } from 'src/domains/layouts/shared/buttons/FormButtons';
import { Loader } from 'src/domains/layouts/shared/loader/Loader';

class State {
    constructor(private readonly topNavigationLinkState: TopNavigationLinkState) {
        makeAutoObservable(this);
    }
    @observable startDateError = false;
    @observable endDateError = false;
    @observable showDirectLinkField = false;

    onChangeStartDate = (date: Date | null): void => {
        if (date !== null) {
            this.topNavigationLinkState.dateFrom.setValue(date.toISOString());
        }
    };

    onBlurStartDate = (date: React.FocusEvent<HTMLInputElement>): void => {
        if (date.target.value === '') {
            this.startDateError = true;
        } else if (this.startDateError) {
            this.startDateError = false;
        }
    };

    onChangeEndDate = (date: Date | null): void => {
        if (date !== null) {
            this.topNavigationLinkState.dateTo.setValue(date.toISOString());
        }
    };

    onBlurEndDate = (date: React.FocusEvent<HTMLInputElement>): void => {
        if (date.target.value === '') {
            this.endDateError = true;
        } else if (this.endDateError) {
            this.endDateError = false;
        }
    };
}

export const NavigationLinkForm = observer(() => {
    const { topNavigationLinkState } = useAppStateContext();
    const [sizeError, setSizeError] = useState(false);

    const [state] = React.useState(() => new State(topNavigationLinkState));
    const {
        isLoading,
        errorMessage,
        dateFrom,
        dateTo,
        isFormValid,
        isEditMode,
        linkName,
        linkNameStatus,
        linkNameModel,
        uploadIconImg,
        orderOption,
        visibilityOption,
        directionOption,
        resetForm,
        handleCancel,
        handleSubmit,
        setDefaultState,
        submitAllowed,
    } = topNavigationLinkState;

    const parsedStartDate = dateFrom.value !== null ? Date.parse(dateFrom.value) : null;
    const startDate = parsedStartDate === null || isNaN(parsedStartDate) ? null : new Date(parsedStartDate);
    const parsedEndDate = dateTo.value !== null ? Date.parse(dateTo.value) : null;
    const endDate = parsedEndDate === null || isNaN(parsedEndDate) ? null : new Date(parsedEndDate);

    const checkSizeOfUploadedImage = (url: string | undefined): void => {
        if (url !== undefined) {
            const img = new Image();
            img.src = url;

            img.onload = function () {
                if (img !== null && img.width !== 16 && img.height !== 16) {
                    setSizeError(true);
                } else if (sizeError === true) {
                    setSizeError(false);
                }
            };
        }
    };

    const clearSizeError = (): void => {
        setSizeError(false);
    };

    const isSubmitDisabled = !isFormValid || sizeError || submitAllowed !== true;
    return (
        <FormWrapper>
            <FormHeader
                isEditMode={isEditMode}
                defaultLabel='Create new link'
                editModeLabel='Edit link'
                resetForm={setDefaultState}
            />
            <FormComponent onSubmit={(e) => e.preventDefault()}>
                <Loader isActive={isLoading} />

                {!!errorMessage && <ErrorMessageBar>{errorMessage}</ErrorMessageBar>}

                <OldSelect
                    onChange={resetForm}
                    selectOptions={directionOptions}
                    stateValue={directionOption}
                    label='Select action'
                    dataTest='select-action'
                />

                <OldInput
                    label='Link Name*'
                    status={linkNameStatus}
                    formInputState={linkName}
                    model={linkNameModel}
                    dataTest='set-link-name'
                />

                <DestinanionOptionsPanel />

                <OldSelect
                    selectOptions={visibilityOptions}
                    stateValue={visibilityOption}
                    label='Choose the place where you want the link to appear'
                />

                <OldSelect
                    selectOptions={orderSelectionListOptions}
                    stateValue={orderOption}
                    label='Select on which place link should appear'
                    defaultText='Select place'
                    dataTest='select-place'
                />

                <UploadImg
                    title='Upload SVG icon (16x16px)'
                    uploadImageState={uploadIconImg}
                    callbackCheckSizeFunction={checkSizeOfUploadedImage}
                    callbackClearFunction={clearSizeError}
                />
                {sizeError === true ? <InputError>Icon has to be 16x16px and use SVG format</InputError> : null}

                <DatesWrapper>
                    <DateInputWrapper>
                        <InputLabel>Start date *</InputLabel>
                        <DatePicker
                            selected={startDate}
                            onChange={state.onChangeStartDate}
                            onBlur={state.onBlurStartDate}
                            showTimeSelect
                            timeFormat='HH:mm'
                            timeIntervals={15}
                            timeCaption='time'
                            dateFormat='MMMM d, yyyy h:mm aa'
                            id='Start-date'
                        />
                        {state.startDateError ? <InputError>This field is required.</InputError> : null}
                    </DateInputWrapper>
                    <DateInputWrapper>
                        <InputLabel>End date *</InputLabel>
                        <DatePicker
                            selected={endDate}
                            onChange={state.onChangeEndDate}
                            onBlur={state.onBlurEndDate}
                            showTimeSelect
                            timeFormat='HH:mm'
                            timeIntervals={15}
                            timeCaption='time'
                            dateFormat='MMMM d, yyyy h:mm aa'
                            id='End-date'
                        />
                        {state.endDateError ? <InputError>This field is required.</InputError> : null}
                    </DateInputWrapper>
                </DatesWrapper>

                <FormButtons
                    onCreate={handleSubmit}
                    onUpdate={handleSubmit}
                    onCancel={handleCancel}
                    isEditMode={isEditMode}
                    isCreateDisabled={isSubmitDisabled}
                    isUpdateDisabled={isSubmitDisabled}
                    dataTestCreate='create-new-navigation-link'
                    dataTestUpdate='update-new-navigation-link'
                />
            </FormComponent>
        </FormWrapper>
    );
});
